import React, { useState, useRef } from "react";
import "./block.scss";
import { convertDatewithTime } from "../../services/Functions";

/**
 * Block component representing a user status block.
 * Displays user details in an info card on hover.
 *
 * @component
 * @memberof Components
 * @param {Object} props - Component properties
 * @param {Object} props.item - User data object
 * @param {string} props.item.isAccountExists - Indicates if the account exists ("1" for yes, "0" for no)
 * @param {boolean} props.item.isLogged_In - Indicates if the user is logged in
 * @param {string} [props.item.subDivisionCode="15171"] - Subdivision code of the user
 * @param {string} [props.item.subDivisionName="MULTAN CANTT"] - Subdivision name of the user
 * @param {string} [props.item.userName="-"] - User name
 * @param {string} [props.item.phoneNo="-"] - Phone number
 * @param {string} [props.item.loggedIn_time] - Last login time
 * @param {string} [props.item.loggedOut_time] - Last logout time
 * @param {string} props.item.companyCode - Company code
 * @param {string} props.item.companyName - Company name
 * @param {string} props.item.circleCode - Circle code
 * @param {string} props.item.circleName - Circle name
 * @param {string} props.item.divisionCode - Division code
 * @param {string} props.item.divisionName - Division name
 *
 * @returns {JSX.Element} A styled block with user information and hover details.
 */
const Block = ({ item }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [positionAbove, setPositionAbove] = useState(false);
  const blockRef = useRef(null);

  /**
  * Handles mouse enter event.
  * Determines whether the info card should be positioned above or below the block.
  */
  const handleMouseEnter = () => {
    if (blockRef.current) {
      const rect = blockRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setPositionAbove(spaceBelow < 450); // If space below is less than 220px, position above
    }
    setIsHovered(true);
  };

  /**
  * Handles mouse leave event.
  * Hides the info card when the mouse leaves the block.
  */
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      className="blockContainer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      ref={blockRef}
    >
      <div
        className={
          item.isAccountExists == "1"
            ? item.isLogged_In
              ? "block-on"
              : "block-off"
            : "block-unknown"
        }
      >
        <p className="minuteNumber">
          {item.subDivisionCode ? item.subDivisionCode : "15171"}
        </p>
      </div>
      <div className="subDivisionName">
        {item.subDivisionName ? item.subDivisionName : "MULTAN CANTT"}
      </div>

      {isHovered && (
        <div className={`infoCard ${positionAbove ? "above" : "below"}`}>
          <div className="infoCard__header">
            <span className="infoCard__code">
              {item.subDivisionCode ? item.subDivisionCode : "—"}
            </span>
            <span
              className={`infoCard__status ${
                String(item.isAccountExists) === "0"
                  ? "infoCard__status--inactive"
                  : item.isLogged_In
                    ? "infoCard__status--online"
                    : "infoCard__status--offline"
              }`}
            >
              {String(item.isAccountExists) === "0"
                ? "Inactive"
                : item.isLogged_In
                  ? "Online"
                  : "Offline"}
            </span>
          </div>
          <div className="infoCard__body">
            <div className="infoCardRow">
              <span className="infoCardLabel">User name</span>
              <span className="infoCardValue">
                {item.userName ? item.userName : "—"}
              </span>
            </div>
            <div className="infoCardRow">
              <span className="infoCardLabel">Phone</span>
              <span className="infoCardValue">
                {item.phoneNo ? item.phoneNo : "—"}
              </span>
            </div>
            <div className="infoCardRow">
              <span className="infoCardLabel">Last login</span>
              <span className="infoCardValue">
                {item.loggedIn_time
                  ? convertDatewithTime(item.loggedIn_time)
                  : "—"}
              </span>
            </div>
            <div className="infoCardRow">
              <span className="infoCardLabel">Last logout</span>
              <span className="infoCardValue">
                {item.loggedOut_time
                  ? convertDatewithTime(item.loggedOut_time)
                  : "—"}
              </span>
            </div>
            <div className="infoCardRow">
              <span className="infoCardLabel">Company</span>
              <span className="infoCardValue">
                {item.companyName || "—"}
              </span>
            </div>
            <div className="infoCardRow">
              <span className="infoCardLabel">Circle</span>
              <span className="infoCardValue">{item.circleName || "—"}</span>
            </div>
            <div className="infoCardRow">
              <span className="infoCardLabel">Division</span>
              <span className="infoCardValue">{item.divisionName || "—"}</span>
            </div>
            <div className="infoCardRow infoCardRow--highlight">
              <span className="infoCardLabel">Sub-division</span>
              <span className="infoCardValue">
                {item.subDivisionName || "—"}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Block;
